import { useMutation } from '@tanstack/react-query';
import Config from 'src/common/Config';

const API_URL = Config.getApiUrl();

type WarmEditSessionArgs = {
  documentId: string;
  clientId: string;
};

/**
 * Eagerly warms the document-edit sandbox when the editor page opens, so the first
 * Siesta instruction doesn't pay the sandbox creation and file-seeding cost.
 * The sandbox is keyed server-side to (document, user) — not the conversation id,
 * which resets whenever the chat context changes — so the warmed workspace survives
 * new chats, discards and page reloads.
 * Best-effort: the backend no-ops when the sandbox editor flag is off, and a failed
 * warmup only costs latency — the first edit request repeats the seeding itself.
 */
export function useWarmEditSession() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({ documentId, clientId }: WarmEditSessionArgs) => {
      const response = await fetch(`${API_URL}/documents/${documentId}/multi-turn-edit/warmup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
      });

      if (response.status < 200 || response.status > 299) throw response;
    },
  });

  const warmEditSession = async (args: WarmEditSessionArgs) => {
    return mutateAsync(args);
  };

  return { warmEditSession };
}
